import { AdminOrder } from '../types';

const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatPrice = (cents: number | null, currency: string) => {
  if (cents === null) return '—';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
};

const TypeBadge = ({ type }: { type: string }) => {
  if (type === 'blueprint')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-500/15 border border-fuchsia-500/25 text-fuchsia-400 whitespace-nowrap">
        <i className="ri-file-download-line text-xs" />
        Blueprint
      </span>
    );
  if (type === 'consultation')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/15 border border-violet-500/25 text-violet-400 whitespace-nowrap">
        <i className="ri-calendar-check-line text-xs" />
        Consult
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-white/50 whitespace-nowrap">
      <i className="ri-shopping-bag-line text-xs" />
      Aankoop
    </span>
  );
};

const PaymentBadge = ({ status }: { status: string }) => {
  if (status === 'paid')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 whitespace-nowrap">
        <i className="ri-shield-check-line text-xs" />
        Betaald
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/25 text-amber-400 whitespace-nowrap">
      <i className="ri-time-line text-xs" />
      {status}
    </span>
  );
};

const BlueprintStatusBadge = ({ sent }: { sent: boolean | null }) => {
  if (sent === null) return null;
  if (sent)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 border border-emerald-500/20 text-emerald-400/80 whitespace-nowrap">
        <i className="ri-mail-check-line text-xs" />
        Verstuurd
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-500/15 border border-rose-500/25 text-rose-400 whitespace-nowrap">
      <i className="ri-mail-close-line text-xs" />
      Niet verstuurd
    </span>
  );
};

interface Props {
  order: AdminOrder;
}

export const AdminOrderRow = ({ order }: Props) => {
  const productLabel =
    order.productName ??
    (order.type === 'consultation' && order.metadata?.consultation_type
      ? `Consult – ${order.metadata.consultation_type}`
      : order.type === 'blueprint'
      ? 'Ritueel Blueprint'
      : 'Aankoop');

  return (
    <div className="bg-[#110020]/60 border border-violet-500/10 rounded-2xl p-4 hover:border-violet-500/25 transition-all duration-200">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        {/* Left: order info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <TypeBadge type={order.type} />
            <PaymentBadge status={order.payment_status} />
            {order.blueprintSent !== null && (
              <BlueprintStatusBadge sent={order.blueprintSent} />
            )}
            <span className="text-white/20 text-xs font-mono">{order.shortId}</span>
          </div>

          <p className="text-white font-semibold text-sm truncate mb-1">{productLabel}</p>

          <div className="flex flex-wrap items-center gap-3">
            {order.email && (
              <div className="flex items-center gap-1.5">
                <i className="ri-mail-line text-violet-400/40 text-xs" />
                <span className="text-white/40 text-xs">{order.email}</span>
              </div>
            )}
            {order.customerName && (
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line text-violet-400/40 text-xs" />
                <span className="text-white/40 text-xs">{order.customerName}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-violet-400/40 text-xs" />
              <span className="text-white/30 text-xs">{formatDate(order.created)}</span>
            </div>
          </div>

          {/* Consultation extra info */}
          {order.type === 'consultation' && order.metadata?.date && (
            <div className="mt-2 flex flex-wrap gap-3">
              <span className="text-white/30 text-xs">
                <span className="text-white/50">Datum:</span> {order.metadata.date}
              </span>
              {order.metadata.time_preference && (
                <span className="text-white/30 text-xs">
                  <span className="text-white/50">Tijd:</span> {order.metadata.time_preference}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: price + actions */}
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-white font-black text-lg">
            {formatPrice(order.amount_total, order.currency)}
          </span>

          <div className="flex items-center gap-2">
            {order.receiptUrl && (
              <a
                href={order.receiptUrl}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-1 px-2.5 py-1.5 bg-white/5 border border-white/10 text-white/50 text-xs rounded-lg hover:border-violet-500/30 hover:text-violet-400 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-receipt-line text-xs" />
                Bon
              </a>
            )}
            <a
              href={`https://dashboard.stripe.com/payments/${order.id}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-violet-600/10 border border-violet-500/20 text-violet-400 text-xs rounded-lg hover:bg-violet-600/20 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line text-xs" />
              Stripe
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
